"use client"
import { MailIcon, PhoneIcon, MapPinIcon } from "lucide-react"
import type { SiteInfo } from "@/lib/site-info"

interface Props { site: SiteInfo }

export default function ContactDropdown({ site }: Props) {
  const mapQuery = encodeURIComponent(`${site.address.full}, ${site.address.country}`)
  return (
    <div className="hidden group-hover:block absolute top-full left-1/2 transform -translate-x-1/2 mt-6 z-50">
      {/* Triangle pointer */}
      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rotate-45 border-t border-l border-gray-200"></div>
      
      {/* Dropdown content */}
      <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
        <div className="w-[250px] sm:w-[350px] md:w-[450px] lg:w-[600px] xl:w-[800px] p-4 lg:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Contact Info Section */}
            <div className="space-y-5">
              <h3 className="text-xl font-semibold text-gray-900">Contact Us</h3>
              
              <div className="flex items-start space-x-3">
                <MapPinIcon className="h-5 w-5 text-primary_green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-700">We are located</h4>
                  <p className="text-gray-600">{site.address.full}, {site.address.country}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <MailIcon className="h-5 w-5 text-primary_green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-700">Email Us</h4>
                  <p className="text-gray-600">{site.email}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <PhoneIcon className="h-5 w-5 text-primary_green flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-700">Phone Us</h4>
                  <p className="text-gray-600">{site.phone}</p>
                </div>
              </div>
            </div>
          
            {/* Contact Form Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Get in Touch</h3>
              <p className="text-sm text-gray-500">Your email address will not be published. Required fields are marked *</p>
              
              <form className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Your name *</label>
                  <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary_green focus:ring-primary_green" />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Your email *</label>
                  <input type="email" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary_green focus:ring-primary_green" />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject *</label>
                  <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary_green focus:ring-primary_green" />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Your message (optional)</label>
                  <textarea rows={3} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary_green focus:ring-primary_green" />
                </div>
                
                <button type="button" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary_green hover:bg-primary_green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary_green">
                  Submit
                </button>
              </form>
            </div>
            
            {/* Map Section */}
            <div>
              <div className="h-full w-full rounded-lg overflow-hidden border border-gray-200">
                <iframe
                  src={`https://maps.google.com/maps?q=${mapQuery}&t=m&z=10&output=embed&iwloc=near`}
                  title={`${site.address.full}, ${site.address.country}`}
                  aria-label={`${site.address.full}, ${site.address.country}`}
                  className="w-full h-full min-h-[300px]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
